import { createContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useToast } from '../hook/useToast';
import httpcommon from '../helper/httpcommon';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { showToast } = useToast();

  const storedUser = localStorage.getItem('login-user');
  const [user, setUser] = useState(storedUser ? JSON.parse(storedUser) : null);

  useEffect(() => {
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [storedUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('login-user');
    setUser(null);
  }, []);

  const getUserInfo = useCallback(
    async (token) => {
      try {
        const response = await httpcommon.get('school/student/profile/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch {
        logout();
        throw new Error('Unable to fetch user profile');
      }
    },
    [logout]
  );

  const login = async (token, role) => {
    try {
      const userInfo = await getUserInfo(token);

      const userData = { token, role, userInfo };
      localStorage.setItem('login-user', JSON.stringify(userData));
      setUser(userData);
    } catch {
      showToast('Failed to login', 'error');
    }
  };

  const refetchUser = useCallback(async () => {
    if (!user?.token) return;

    try {
      const userInfo = await getUserInfo(user.token);
      const updatedUser = { ...user, userInfo };

      localStorage.setItem('login-user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch {
      showToast('Failed to refresh user data', 'error');
    }
  }, [getUserInfo, showToast, user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
